/**
 * <plp-gallery> — crossfading image gallery with pagination dots. Hooks are
 * data attributes, not section classes, so any PLP section can use it. Slides
 * crossfade in place rather than scroll, which keeps the frame height fixed.
 *
 * Markup: [data-plp-gallery-slide] slides, [data-plp-gallery-dot] dots, and
 * optional data-plp-gallery-autoplay / -interval on the host.
 */
class PlpGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.slides = Array.from(this.querySelectorAll('[data-plp-gallery-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-plp-gallery-dot]'));
    if (this.slides.length < 2) return;

    this.index = 0;
    this.visible = true;
    this.interval = (parseFloat(this.dataset.plpGalleryInterval) || 6) * 1000;
    this.autoplay = this.dataset.plpGalleryAutoplay === 'true';

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.go(i);
        this.restart();
      });
    });

    this.observeViewport();
    this.go(0);
    this.restart();
  }

  disconnectedCallback() {
    this.stop();
    if (this.viewportObserver) this.viewportObserver.disconnect();
  }

  go(index) {
    this.index = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });
  }

  next() {
    this.go(this.index + 1);
  }

  shouldAutoplay() {
    if (!this.autoplay || !this.visible) return false;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  restart() {
    this.stop();
    if (!this.shouldAutoplay()) return;
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  observeViewport() {
    if (!('IntersectionObserver' in window)) return;

    this.viewportObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.visible = entry.isIntersecting;
        this.restart();
      });
    }, { threshold: 0 });

    this.viewportObserver.observe(this);
  }
}

customElements.define('plp-gallery', PlpGallery);
